import { useState, useEffect, useRef } from "react";

const C = {
  navy: "#0E1A2E", red: "#C8302A", green: "#0F8B3D", amber: "#E8A317",
  white: "#FFFFFF", cream: "#FAF7F1", border: "#E0D9C8", line: "#EBE4D2",
  text: "#1A1A1A", text2: "#3A3A3A", text3: "#6A6A6A", text4: "#9A9A9A",
  errorBg: "#FEF2F2", errorBorder: "#FECACA", errorText: "#991B1B",
  successBg: "#F0FDF4", successBorder: "#BBF7D0", successText: "#166534",
};

// ── Stable input component — no DOM mutation, no rerender flicker ──────────
export function TextField({
  label, value, onChange, type = "text", placeholder, hint, error,
  required, autoFocus, autoComplete, onEnter, disabled, maxLength,
  prefix, suffix, format,
}) {
  const ref = useRef(null);
  const [focused, setFocused] = useState(false);

  const handleChange = (e) => {
    let val = e.target.value;
    if (format === "phone") val = formatPhone(val);
    if (format === "pin") val = val.replace(/\D/g, "").slice(0, 6);
    if (format === "digits") val = val.replace(/\D/g, "");
    if (format === "upper") val = val.toUpperCase();
    onChange(val);
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && onEnter) {
      e.preventDefault();
      onEnter();
    }
  };

  const borderColor = error ? C.errorBorder : focused ? C.red : C.border;
  const bgColor = focused ? C.white : C.cream;

  return (
    <div style={{ marginBottom: 14 }}>
      {label && (
        <label style={{
          fontSize: 12, color: C.text2, fontWeight: 600,
          display: "block", marginBottom: 6,
        }}>
          {label} {required && <span style={{ color: C.red }}>*</span>}
        </label>
      )}
      <div style={{
        display: "flex", alignItems: "center",
        border: `1px solid ${borderColor}`, borderRadius: 7,
        background: bgColor, transition: "border-color 0.15s, background 0.15s",
        boxShadow: focused ? `0 0 0 3px ${C.red}15` : "none",
      }}>
        {prefix && (
          <div style={{
            padding: "12px 0 12px 14px", color: C.text3,
            fontSize: 14, fontFamily: "inherit", userSelect: "none",
          }}>
            {prefix}
          </div>
        )}
        <input
          ref={ref}
          type={type}
          value={value || ""}
          onChange={handleChange}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onKeyDown={handleKey}
          placeholder={placeholder}
          required={required}
          autoFocus={autoFocus}
          autoComplete={autoComplete}
          disabled={disabled}
          maxLength={maxLength}
          style={{
            flex: 1, width: "100%",
            padding: prefix ? "12px 14px 12px 8px" : "12px 14px",
            border: "none", outline: "none",
            background: "transparent",
            fontSize: 14, fontFamily: "inherit",
            color: C.text,
          }}
        />
        {suffix && (
          <div style={{
            padding: "12px 14px 12px 0", color: C.text3,
            fontSize: 14, userSelect: "none",
          }}>
            {suffix}
          </div>
        )}
      </div>
      {error ? (
        <div style={{ fontSize: 12, color: C.errorText, marginTop: 4 }}>{error}</div>
      ) : hint ? (
        <div style={{ fontSize: 11, color: C.text4, marginTop: 4, lineHeight: 1.5 }}>{hint}</div>
      ) : null}
    </div>
  );
}

// ── Format Indian phone as you type — ignores existing +91 prefix ──────────
function formatPhone(raw) {
  if (!raw) return "";
  // Strip everything except digits and a leading +
  let v = raw.startsWith("+") ? "+" + raw.slice(1).replace(/\D/g, "") : raw.replace(/\D/g, "");
  // If user typed local 10-digit number, leave as-is for display
  if (!v.startsWith("+") && v.length <= 10) return v.replace(/(\d{5})(\d{0,5})/, "$1 $2").trim();
  // If user typed +91 followed by 10 digits, format as +91 XXXXX XXXXX
  if (v.startsWith("+91")) {
    const rest = v.slice(3);
    if (rest.length <= 5) return `+91 ${rest}`;
    return `+91 ${rest.slice(0, 5)} ${rest.slice(5, 10)}`.trim();
  }
  return v;
}

export function normalizePhone(formatted) {
  if (!formatted) return "";
  let v = formatted.replace(/\s/g, "");
  if (!v.startsWith("+")) {
    if (v.startsWith("91") && v.length === 12) v = `+${v}`;
    else if (v.length === 10) v = `+91${v}`;
  }
  return v;
}

export function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email || "");
}

export function isValidPhone(phone) {
  const v = normalizePhone(phone);
  return /^\+91\d{10}$/.test(v);
}

// ── Banner for error/success messages ──────────────────────────────────────
export function Banner({ kind = "error", children, onDismiss }) {
  const styles = {
    error:   { bg: C.errorBg,   border: C.errorBorder,   text: C.errorText,   icon: "⚠" },
    success: { bg: C.successBg, border: C.successBorder, text: C.successText, icon: "✓" },
    info:    { bg: "#EFF6FF",   border: "#BFDBFE",       text: "#1E40AF",     icon: "ℹ" },
  }[kind];
  return (
    <div style={{
      background: styles.bg, border: `1px solid ${styles.border}`,
      color: styles.text, padding: "12px 14px", borderRadius: 8,
      fontSize: 13, marginBottom: 16, lineHeight: 1.5,
      display: "flex", gap: 10, alignItems: "flex-start",
    }}>
      <span style={{ fontWeight: 700, marginTop: 1 }}>{styles.icon}</span>
      <div style={{ flex: 1 }}>{children}</div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          style={{ background: "none", border: "none", color: styles.text, cursor: "pointer", fontSize: 16, padding: 0, opacity: 0.6 }}
        >×</button>
      )}
    </div>
  );
}

// ── Primary button ──────────────────────────────────────────────────────────
export function Button({ children, onClick, disabled, busy, variant = "primary", style = {}, type = "button", fullWidth = true }) {
  const variants = {
    primary:   { bg: C.red,    color: C.white, border: "none" },
    secondary: { bg: "transparent", color: C.text2, border: `1px solid ${C.border}` },
    ghost:     { bg: "transparent", color: C.red,   border: "none" },
    nav:       { bg: C.navy,   color: C.white, border: "none" },
  }[variant];
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || busy}
      style={{
        width: fullWidth ? "100%" : "auto",
        background: disabled ? C.text4 : variants.bg,
        color: variants.color,
        border: variants.border,
        padding: "13px 22px", borderRadius: 8,
        fontSize: 14, fontWeight: 700,
        cursor: disabled || busy ? "not-allowed" : "pointer",
        fontFamily: "inherit",
        transition: "opacity 0.15s, transform 0.05s",
        ...style,
      }}
      onMouseDown={(e) => { if (!disabled && !busy) e.currentTarget.style.transform = "scale(0.99)"; }}
      onMouseUp={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
    >
      {busy ? "Loading…" : children}
    </button>
  );
}

// ── Verify Modal — used for phone + email verification ─────────────────────
export function VerifyModal({ open, onClose, kind, identifier, onVerified }) {
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState("send"); // send | enter | done
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (open && step === "send") {
      sendOtp();
    }
    if (!open) {
      setOtp(""); setStep("send"); setError(null); setResendIn(0);
    }
    // eslint-disable-next-line
  }, [open]);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn(resendIn - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const sendOtp = async () => {
    setBusy(true); setError(null);
    try {
      const tok = localStorage.getItem("shipswift_jwt");
      const r = await fetch(`/api/v1/auth/send-otp?channel=${kind}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: tok ? `Bearer ${tok}` : "" },
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(data.detail || `Could not send code. Try again in a moment.`);
      }
      setStep("enter");
      setResendIn(30);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    if (otp.length !== 6) { setError("Enter the 6-digit code."); return; }
    setBusy(true); setError(null);
    try {
      const tok = localStorage.getItem("shipswift_jwt");
      const r = await fetch(`/api/v1/auth/verify-otp?channel=${kind}&otp=${otp}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: tok ? `Bearer ${tok}` : "" },
      });
      if (!r.ok && r.status !== 204) {
        const data = await r.json().catch(() => ({}));
        throw new Error(data.detail || "Invalid or expired code.");
      }
      setStep("done");
      setTimeout(() => { onVerified && onVerified(); onClose(); }, 1200);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 100,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: C.white, borderRadius: 14, padding: 28, maxWidth: 420, width: "100%",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
          <h3 style={{ fontSize: 19, fontWeight: 800, color: C.navy, margin: 0 }}>
            Verify your {kind}
          </h3>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 22, color: C.text3, cursor: "pointer" }}>×</button>
        </div>

        {step === "done" ? (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: C.green }}>{kind === "phone" ? "Phone" : "Email"} verified!</div>
          </div>
        ) : (
          <>
            <p style={{ fontSize: 14, color: C.text2, marginBottom: 18 }}>
              {step === "send" ? `We'll send a 6-digit code to ${identifier}` : `We've sent a 6-digit code to ${identifier}.`}
            </p>
            {error && <Banner kind="error">{error}</Banner>}

            {step === "enter" && (
              <TextField
                label="Verification code"
                value={otp}
                onChange={setOtp}
                format="digits"
                maxLength={6}
                placeholder="123456"
                onEnter={verify}
                autoFocus
              />
            )}

            <Button onClick={step === "send" ? sendOtp : verify} busy={busy} disabled={step === "enter" && otp.length !== 6}>
              {step === "send" ? "Send code" : "Verify"}
            </Button>

            {step === "enter" && (
              <div style={{ textAlign: "center", marginTop: 12, fontSize: 13, color: C.text3 }}>
                {resendIn > 0 ? (
                  <span>Resend code in {resendIn}s</span>
                ) : (
                  <button type="button" onClick={sendOtp} style={{
                    background: "none", border: "none", color: C.red,
                    fontWeight: 600, cursor: "pointer", fontSize: 13,
                  }}>
                    Resend code
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export const COLORS = C;
